import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {createPortal} from 'react-dom';
import {Icon} from '@iconify/react';
import {useQueryClient} from '@tanstack/react-query';
import {SORT} from '../../../../incase-domain/common/sort.enum';
import {useBulkRemoveRooms, useFetchRooms, useRemoveRoom, useDuplicateRooms} from '../queries';
import {QUERY_KEYS} from '../../../../utils/queryKeys';
import {IFetchRoom, IFetchRoomPaginated} from '../../../../incase-domain/rooms/room.editor';
import DataView, {ViewMode} from '../../../components/common/data/DataView';
import {createDataViewListStateProps} from '../../../../hooks/listParams';
import {useRoomsListSearchParams} from '../roomsListSearchParams';
import {RoomStatus} from '../../../../incase-domain/rooms/room.player';
import DropdownMenuCompact, {
    DropdownMenuCompactMenuPosition,
} from '../../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import {createPlayPresentationUrl} from '../../../../utils/presentation';
import {selectPresentationToEditAction} from '../../../../store/actions/presentation';
import Button from '../../../../incase-domain/components/button/Button';
import EditRoomModal from '../editRoom/EditRoomModal';
import ErrorModalWrapper from '../../../../incase-domain/components/modals/errorModal/ErrorModalWrapper';
import ErrorModalContent from '../../../../incase-domain/components/modals/errorModal/ErrorModalContent';
import ErrorModalCloseButton from '../../../../incase-domain/components/modals/errorModal/ErrorModalCloseButton';
import CreatePresentationLinksModals from '../../../components/presentationList/presentationLinks/CreatePresentationLinksModals';
import {useRoomSocket} from '../../../../utils/hooks/useRoomSocket';
import {stopRoom} from '../../../../service/room.service';
import {prettyDateFormat} from '../../../../utils/dateTimeUtils';
import ProtectionTypeIcons from '../../../components/ProtectionTypeIcons';
import ConfirmModal from '../../../../incase-domain/components/modals/confirmModal/ConfirmModal';

interface RoomsListProps {
    onCreateRoom: () => void;
}

function RoomsListTable({onCreateRoom}: RoomsListProps): JSX.Element {
    const {t, i18n} = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const removeRoomMutation = useBulkRemoveRooms({onDeletePageChangeHandler});
    const removeSingleRoomMutation = useRemoveRoom({onDeletePageChangeHandler});
    const {mutate: duplicateRoomsMutation, isLoading: isDuplicating} = useDuplicateRooms();

    const [selectedRoomForEdit, setSelectedRoomForEdit] = React.useState<string | undefined>(undefined);
    const [openDeleteRoomModal, setOpenDeleteRoomModal] = useState(false);
    const [deleteRooms, setDeleteRooms] = useState<IFetchRoom[]>([]);
    const [selectedRoomForLinks, setSelectedRoomForLinks] = useState<IFetchRoomPaginated>();
    const [terminateRoomModal, setTerminateRoomModal] = useState<boolean>(false);
    const [roomToTerminate, setRoomToTerminate] = useState<IFetchRoom | null>(null);
    const [roomPresenterRemovedModal, setRoomPresenterRemovedModal] = useState(false);
    const [deleteRoom, setDeleteRoom] = useState<IFetchRoomPaginated>({} as IFetchRoomPaginated);
    const {listParams, setListParams} = useRoomsListSearchParams();

    const dataViewListState = createDataViewListStateProps(listParams, setListParams);

    useRoomSocket(roomToTerminate?._id);

    const fetchRooms = useFetchRooms({
        pageNumber: listParams.page,
        pageSize: listParams.size,
        searchTerm: listParams.search,
        sortBy: listParams.sortBy,
    });

    const handleTerminateRoom = (room: IFetchRoom): void => {
        setRoomToTerminate(room);
        queryClient.invalidateQueries({queryKey: QUERY_KEYS.room.lists()});
        setTerminateRoomModal(true);
    };

    const handleDuplicateRoom = (roomId: string): void => {
        duplicateRoomsMutation([roomId]);
    };

    function onDeletePageChangeHandler(deletedCount = 1): void {
        let newElCount = 0;
        let newMaxPage = 0;

        if (fetchRooms.data) {
            newElCount = fetchRooms.data.numberOfElements - deletedCount;
            newMaxPage = Math.max(Math.ceil(newElCount / fetchRooms.data.pageSize), 1);
        }

        if (listParams.page > newMaxPage) {
            setListParams({page: newMaxPage});
        }
    }

    const renderName = (room: IFetchRoom): JSX.Element => {
        return <>{room.roomName}</>;
    };

    const renderPresentation = (room: IFetchRoom): JSX.Element => {
        return <>{room.presentation.name}</>;
    };

    const renderProtection = (room: IFetchRoom): JSX.Element => {
        return <ProtectionTypeIcons protectionTypes={room.protection.types} />;
    };

    const renderLastModified = (room: IFetchRoom): JSX.Element => {
        return <>{prettyDateFormat(new Date(room.lastModified), i18n.language)}</>;
    };

    const renderStatus = (room: IFetchRoom): JSX.Element => {
        const getClass = (roomStatus: RoomStatus): string => {
            switch (roomStatus) {
                case RoomStatus.PLAYING:
                    return 'is-playing';
                case RoomStatus.LOBBY:
                    return 'is-lobby';
                default:
                    return 'is-idle';
            }
        };

        return (
            <Button className={`c-pill ${getClass(room.roomStatus)}`} size="xs" disablePointerEvents>
                {t(`roomsPage.status.${room.roomStatus || RoomStatus.IDLE}`)}
            </Button>
        );
    };

    const renderActions = (room: IFetchRoom): JSX.Element => {
        return (
            <div
                onClick={(e) => {
                    e.stopPropagation();
                }}
                data-cy="room_actions"
            >
                <DropdownMenuCompact
                    menuPosition={DropdownMenuCompactMenuPosition.LEFT}
                    items={[
                        [
                            {
                                label: t('roomsPage.actions.preview'),
                                icon: 'solar:play-linear',
                                onClick: () => window.open(createPlayPresentationUrl(room.presentation), '_blank'),
                                dataCy: 'preview_presentation',
                            },
                            {
                                label: t('roomsPage.actions.stop'),
                                icon: 'solar:stop-linear',
                                onClick: () => handleTerminateRoom(room),
                                disabled: !room.roomStatus || room.roomStatus === RoomStatus.IDLE,
                            },
                        ],
                        [
                            {
                                label: t('roomsPage.actions.reports'),
                                icon: 'solar:chart-linear',
                                onClick: () => {
                                    dispatch(selectPresentationToEditAction(room.presentation._id, navigate));
                                    navigate(`/presentations/${room.presentation._id}/report`, {state: {roomId: room._id}});
                                },
                            },
                            {
                                label: t('roomsPage.actions.share'),
                                icon: 'solar:link-linear',
                                onClick: () => setSelectedRoomForLinks(room),
                                disabled: !room.protection?.types.length,
                                dataCy: 'share_presentation',
                            },
                            {
                                label: t('roomsPage.actions.settings'),
                                icon: 'solar:settings-linear',
                                onClick: () => setSelectedRoomForEdit(room._id),
                                dataCy: 'edit_room',
                            },
                            {
                                label: t('roomsPage.actions.duplicate'),
                                icon: 'solar:copy-linear',
                                onClick: () => handleDuplicateRoom(room._id),
                                dataCy: 'duplicate_room',
                                disabled: isDuplicating,
                            },
                        ],
                        [
                            {
                                label: t('roomsPage.actions.delete'),
                                icon: 'solar:trash-bin-minimalistic-linear',
                                onClick: () => setDeleteRoom(room),
                                dataCy: 'delete_room',
                                color: 'error',
                            },
                        ],
                    ]}
                >
                    <Button
                        leading="ph:dots-three-vertical-bold"
                        size="sm"
                        variant="ghost"
                        square
                        data-cy="moreActions"
                        aria-label="Toggle show all action dropdown"
                    />
                </DropdownMenuCompact>
            </div>
        );
    };

    return (
        <>
            <div className="c-presentationName" data-cy="rooms-list">
                <DataView
                    views={[
                        {
                            view: ViewMode.TABLE,
                            columns: [
                                {
                                    label: t('roomsPage.table.roomName'),
                                    key: 'roomName',
                                    direction: listParams.sortBy?.roomName || SORT.DESC,
                                    sortable: true,
                                    body: (row) => renderName(row),
                                },
                                {
                                    label: t('roomsPage.table.presentationName'),
                                    body: (row) => renderPresentation(row),
                                    width: '16rem',
                                },
                                {
                                    label: t('roomsPage.table.protection'),
                                    body: (row) => renderProtection(row),
                                    width: '7rem',
                                },
                                {
                                    label: t('roomsPage.table.lastModified'),
                                    key: 'lastModified',
                                    direction: listParams.sortBy?.lastModified || SORT.DESC,
                                    sortable: true,
                                    body: (row) => renderLastModified(row),
                                    width: '11rem',
                                },
                                {
                                    label: t('roomsPage.table.status'),
                                    body: (row) => renderStatus(row),
                                    width: '7rem',
                                },
                                {
                                    body: (row) => renderActions(row),
                                    width: '4rem',
                                },
                            ],
                        },
                    ]}
                    rows={fetchRooms.data?.content || []}
                    rowIdentifier={(row) => row._id}
                    loading={fetchRooms.isFetching || fetchRooms.isLoading}
                    onRowClick={(row) => {
                        setSelectedRoomForEdit(row._id);
                    }}
                    enableSearch
                    selectActions={[
                        {
                            label: t('editorPage.delete'),
                            onClick: (selected: IFetchRoom[]) => {
                                setOpenDeleteRoomModal(true);
                                setDeleteRooms(selected);
                            },
                            icon: 'solar:trash-bin-minimalistic-linear',
                            color: 'error',
                        },
                        {
                            label: t('roomsPage.actions.duplicate'),
                            onClick: (selected: IFetchRoom[]) => {
                                duplicateRoomsMutation(selected.map(({_id}) => _id));
                            },
                            icon: 'solar:copy-linear',
                            color: 'primary',
                            disabled: isDuplicating,
                        },
                    ]}
                    paginationData={{
                        pageSize: listParams.size,
                        currentPage: listParams.page,
                        itemsCount: fetchRooms.data?.numberOfElements || 0,
                    }}
                    {...dataViewListState}
                    headerActionPrimary={{icon: 'ph:plus-bold', onClick: onCreateRoom, title: t('genericTable.new'), dataCy: 'create_room'}}
                />
            </div>
            <ConfirmModal
                message={t('roomsPage.modals.deleteRooms')}
                color="error"
                isOpen={openDeleteRoomModal}
                onCancel={() => setOpenDeleteRoomModal(false)}
                onConfirm={() => {
                    removeRoomMutation.mutate(deleteRooms.map(({_id}) => _id));
                    setOpenDeleteRoomModal(false);
                    setDeleteRooms([]);
                }}
                showCancel
                showConfirm
                cancelText={`${t('common.cancel')}`}
                confirmText={`${t('common.confirm')}`}
            />
            <EditRoomModal roomId={selectedRoomForEdit} onCancel={() => setSelectedRoomForEdit(undefined)} />
            {selectedRoomForLinks &&
                createPortal(
                    <CreatePresentationLinksModals
                        open={!!selectedRoomForLinks}
                        closeModal={() => setSelectedRoomForLinks(undefined)}
                        presentation={selectedRoomForLinks.presentation}
                        roomId={selectedRoomForLinks._id}
                        presenterLinkCode={selectedRoomForLinks.presenterLinkCode}
                    />,
                    document.body
                )}

            {createPortal(
                <ConfirmModal
                    message={`${t('roomsPage.modals.deleteRoom')} ${deleteRoom.roomName}?`}
                    color="error"
                    isOpen={!!deleteRoom._id}
                    onCancel={() => setDeleteRoom({} as IFetchRoomPaginated)}
                    onConfirm={() => {
                        removeSingleRoomMutation.mutate(deleteRoom._id);
                        setDeleteRoom({} as IFetchRoomPaginated);
                    }}
                    showCancel
                    showConfirm
                    confirmText={t('common.confirm')}
                    cancelText={t('common.cancel')}
                    dataCy="delete_room_modal"
                />,
                document.body
            )}

            {createPortal(
                <ConfirmModal
                    color="error"
                    message={t('roomsPage.modals.removePresenterConfirmation')}
                    isOpen={terminateRoomModal && !!roomToTerminate}
                    onCancel={() => {
                        setTerminateRoomModal(false);
                        setRoomToTerminate(null);
                    }}
                    onConfirm={() => {
                        if (roomToTerminate) {
                            stopRoom();
                            setTerminateRoomModal(false);
                            setRoomPresenterRemovedModal(true);
                            queryClient.invalidateQueries({queryKey: QUERY_KEYS.room.lists()});
                            setRoomToTerminate(null);
                        }
                    }}
                    showCancel
                    showConfirm
                    confirmText={t('common.confirm')}
                    cancelText={t('common.cancel')}
                />,
                document.body
            )}

            {createPortal(
                <ErrorModalWrapper isOpen={roomPresenterRemovedModal} setIsOpen={setRoomPresenterRemovedModal}>
                    <Icon icon="ooui:success" fontSize="5rem" color="#48c774" />
                    <ErrorModalContent
                        title={t('roomsPage.modals.roomPresenterRemoved')}
                        description={t('roomsPage.modals.roomPresenterRemovedSuccess')}
                        className="c-successModalContent"
                        titleClassName="c-successModalContent__title"
                    />
                    <ErrorModalCloseButton onClose={() => setRoomPresenterRemovedModal(false)} className="c-successModalCloseButton" />
                </ErrorModalWrapper>,
                document.body
            )}
        </>
    );
}

export default RoomsListTable;
